<template>
  <config-base-comp
    v-bind="$props"
    :after-discount-amount="afterSingleDiscountAmount"
    :origin-amount="singleOriginAmount"
    :show-discount-price="showDiscountPrice"
    class="opc-partner-base">
    <alert-message slot="topAlert" />
    <div ref="config" slot="config" class="opc-partner-base__config">
      <div class="opc-partner-base__license">
        <input-number-with-popover
          v-model="selectedNumber"
          :increase-tips="increaseTips"
          :label="`${title} Select the number of users`"
          :max="maxHosts"
          :min="minHosts"
          class="opc-partner-base__quantity-selector"
          controls-position="both"
          size="large"
          @change="updatePlanLicense" />
        <price-comp
          :after-discount-amount="afterSingleDiscountAmount"
          :discount-percentage="discountPercentage"
          :origin-amount="singleOriginAmount"
          :show-discount-price="showDiscountPrice"
        />
      </div>
      <!-- for add addon flow display information -->
      <alert v-if="mx_licenseAlert" class="opc-partner-base__license-alert" type="info">
        <p class="opc-partner-base__license-alert-tips" v-html="mx_licenseAlert" />
      </alert>
      <preview-payment class="opc-partner-base__preview-payment" />
    </div>
    <template #footer>
      <p class="opc-partner-base__footer-tips">
        {{ $t('billing.partner.masterclass.provide_tips') }}
      </p>
      <zm-button
        v-if="showRemove"
        data-testid="checkout-button-69"
        :aria-label="`remove ${title} plan`"
        class="opc-partner-base__footer-btn"
        ghost
        type="link"
        @click="removePlanByKey">
        {{ $t('billing.common.remove') }}
      </zm-button>
    </template>
  </config-base-comp>
</template>

<script type="text/jsx">
import _ from 'lodash';
import { mapGetters, mapMutations } from 'vuex';
import ConfigBase from '@/components/config-base';
import { plansof } from '@/utils/chain';
import mx_plansCommon from '@/plans/mx/plan';
import mx_config from '@/plans/MASTER_CLASS/mixin';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import PartnerAlertMessage from '@/views/config/partner-refer/component/alert-message';
import SaveCount from '@/components/save-count';
import Alert from '@/components/zoom-ui-plus/alert/alert.vue';
import { planUnitMap } from '@/common/product-unit';
import { isEmptyVal } from '@/utils';
import PreviewPayment from '@/views/checkout/payment/preview-payment';

const SaveCountComp = {
  extends: SaveCount,
  props: {
    annualPrice: Number,
    monthlyPrice: Number,
    percentage: Number,
  },
  computed: {
    savedPercent() {
      return this.percentage ? `${this.percentage}%` : '';
    },
  },
};

const ConfigBaseComp = {
  extends: ConfigBase,
  mixins: [mx_config],
  props: {
    originAmount: Number,
    afterDiscountAmount: Number,
    showDiscountPrice: Boolean,
  },
  data() {
    return {
      showLearnMore: false,
      unit: planUnitMap.membership,
    };
  },
  methods: {
    mx_renderCycle() {
      const amount = this.showDiscountPrice ? this.afterDiscountAmount : this.originAmount;

      return <li>
        <span>
          {this.$t('billing.partner.masterclass.cycle_amount', [this.$money(amount, { forceDiff: true }), this.planCycleValue])}
        </span>
      </li>;
    },
  },
};

const AlertMessage = {
  extends: PartnerAlertMessage,
  methods: {
    renderEligibleIcon() {
      return <img
        class="opc-partner-base__alert-eligible--icon"
        src={require('@/assets/Gift.svg')}
        alt="" />;
    },
  },
  render() {
    if (!this.isFromPartnerRefer) return;

    const contentNode = this.renderContent();

    return <div class="opc-partner-refer opc-partner-base__alert">
      {contentNode}
    </div>;
  },
};

const PriceComp = {
  components: { SaveCountComp },
  props: {
    originAmount: Number,
    afterDiscountAmount: Number,
    discountPercentage: Number,
    showDiscountPrice: Boolean,
  },
  methods: {
    mx_discount() {
      const afterDiscountAmount = this.$money(this.afterDiscountAmount, { forceDiff: true });
      const beforeDiscountAmount = this.$money(this.originAmount, { forceDiff: true });
      const discountPercentage = this.discountPercentage;

      return <div class="opc-partner-base__license-row"
                  aria-label={`x ${afterDiscountAmount} regular price ${beforeDiscountAmount} saved ${discountPercentage}%`}>
        <span class="opc-partner-base__license-discount-price" aria-hidden="true">x {afterDiscountAmount}</span>
        <del class="opc-partner-base__license-origin-price" aria-hidden="true">{beforeDiscountAmount}</del>
        <save-count-comp aria-hidden="true" percentage={discountPercentage} />
      </div>;
    },
    mx_price() {
      const originalAmount = this.$money(this.originAmount, { forceDiff: true });

      return <div class="opc-partner-base__license-row" aria-label={`regular price ${originalAmount}`}>
        <span class="opc-partner-base__license-discount-price" aria-hidden="true">x {originalAmount}</span>
      </div>;
    },
  },
  render() {
    return this.showDiscountPrice ? this.mx_discount() : this.mx_price();
  },
};

export default {
  name: 'MASTERCLASS',
  mixins: [mx_config, mx_plansCommon],
  components: {
    PreviewPayment,
    ConfigBaseComp,
    InputNumberWithPopover,
    AlertMessage,
    Alert,
    PriceComp,
  },
  data() {
    return {
      selectedNumber: 1,
    };
  },
  computed: {
    ...mapGetters(['planMapFlat', 'getUserCurrentSubscriptions', 'getSelectedPlans', 'discountWithAmountMap', 'eligiblePartnerReferOffer']),
    selectedPlan() {
      return plansof(this.getSelectedPlans).type(this.planType).get(0);
    },
    minHosts() {
      return this.plan?.min;
    },
    licenseDepPlan() {
      const { licenseDependBasePlanTypeList = [] } = this.plan || {};
      return _.find(this.getUserCurrentSubscriptions, (p) => _.includes(licenseDependBasePlanTypeList, p.type)) || {};
    },
    depLicenseQuantity() {
      return _.get(this.licenseDepPlan, 'cartInfo.selected_number', 1);
    },
    maxHosts() {
      return Math.min(this.depLicenseQuantity, this.plan?.max);
    },
    mx_licenseAlert() {
      return '';
    },
    mx_increaseTips() {
      return this.selectedNumber >= this.maxHosts ? this.$t('billing.partner.masterclass.license_alert', [this.planTitle, this.$t(this.licenseDepPlan?.typeNameKey)]) : '';
    },
    increaseTips() {
      return this.mx_increaseTips;
    },
    showDiscountPrice() {
      return this.eligiblePartnerReferOffer && !!this.singleDiscountAmount;
    },
    showRemove() {
      return true;
    },
  },
  methods: {
    ...mapMutations(['updateSelectPlan']),
    updatePlanLicense: _.debounce(function(newQuantity, oldQuantity) {
      if (newQuantity) {
        this.updateSelectPlan({
          plan_code: this.selectedPlan?.plan_code,
          cartInfo: { selected_number: newQuantity },
        });

        // event tracking
        this.$tracking.buyInteraction(
          `number of user`,
          `${this.title} - ${newQuantity > oldQuantity ? 'increase' : 'decrease'} - ${newQuantity}`,
        );
      }
    }, 200),
  },
  created() {
    if (isEmptyVal(this.selectedPlan) && this.plan) {
      this.updateSelectPlan({
        plan_code: this.plan?.plan_code,
        cartInfo: { selected_number: 1 },
      });
    }
  },
  watch: {
    selectedPlan: {
      immediate: true,
      handler(p) {
        const quantity = p?.cartInfo?.selected_number;
        quantity && (this.selectedNumber = quantity);
      },
    },
  },
};
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-partner-base {
  &__config {

  }

  @include media(mobile) {
    .opc-foldable {
      .opc-zm-base-card__name {
        padding-right: 20px;
      }

      &.is-active {
        .opc-zm-base-card__name {
          padding-right: 0px;
        }
      }
    }
  }

  .opc-zm-base-card {
    &__main__footer {
      display: flex;
      align-content: center;
      justify-content: space-between;
      align-items: center;
    }
  }

  &__license {
    display: flex;
    align-items: center;

    &-row {
      display: flex;
      align-items: center;
      margin-left: 12px;

      @include media(mobile) {
        margin-left: 8px;
        flex-wrap: wrap;
      }
    }

    &-origin-price {
      color: #515079;
      font-size: 12px;
      font-style: normal;
      font-weight: 600;
      line-height: 150%;
      margin: 2px 4px 0px;
    }

    &-discount-price {
      color: #00031F;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: 150%;
    }

    &-alert {
      &.zm-alert {
        margin-top: 24px;
      }

      &-tips {
        color: #474750;
        font-size: 12px;
        font-weight: 500;
        line-height: 150%; /* 21px */

        a {
          color: #0B5CFF;
          font-weight: 600;
        }
      }
    }
  }

  &__footer {
    &-tips {
      color: #666487;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 120%; /* 16.8px */

      @include media(mobile) {
        font-size: 12px;
        text-align: left;
      }
    }

    &-btn {
      &.zm-button {
        &.zm-button--link {
          padding: 0px !important;
        }
      }
    }
  }

  &__preview-payment {
    margin-top: 24px;
  }

  @include media(mobile) {
    .opc-price-save-count {
      font-size: 12px !important;
    }
  }
}

/** rewrite partner refer css style */
.opc-partner-base__alert {
  .opc-partner-refer__eligible {
    border-radius: 8px;
    background: #E2FBEA;
    box-shadow: unset;

    &::after {
      display: none;
    }

    &--content {
      padding-right: 0px;
    }
  }

  .opc-partner-refer__ineligible {
    border-radius: 8px;
  }

  &-eligible--icon {
    margin-right: 16px;
  }
}
</style>
